import { Preferences, WorkspaceDocument } from "../types";
import { inferTitle } from "./markdown";
import { sampleMarkdown } from "../data/sampleMarkdown";

// Legacy keys — read once to migrate old data into the workspace.
const DRAFT_KEY = "markdown-studio:draft";
const RECENTS_KEY = "markdown-studio:recents";

const PREFS_KEY = "markdown-studio:preferences";
const WORKSPACE_KEY = "markdown-studio:workspace";
// Desktop only: the vault folder to reopen on launch.
const VAULT_KEY = "markdown-studio:vault-root";

export const createId = (): string => crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export interface WorkspaceState {
  documents: WorkspaceDocument[];
  activeId: string;
}

export const defaultPreferences: Preferences = {
  theme: "system",
  editorFontSize: 15,
  tabSize: 2,
  wordWrap: true,
  showLineNumbers: true,
  spellcheck: false,
  syncScroll: true,
  previewFontSize: 15.5,
  previewWidth: "comfortable",
  previewFont: "sans",
  enableMath: true,
  pageSize: "a4",
  margins: 18,
  exportTitle: "Untitled document",
  includePageNumbers: true,
  exportTheme: "light",
};

export const loadPreferences = (): Preferences => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...defaultPreferences, ...JSON.parse(raw) } : defaultPreferences;
  } catch {
    return defaultPreferences;
  }
};

export const savePreferences = (preferences: Preferences) => {
  localStorage.setItem(PREFS_KEY, JSON.stringify(preferences));
};

const freshWorkspace = (): WorkspaceState => {
  const doc: WorkspaceDocument = { id: createId(), name: "Markdown Studio", content: sampleMarkdown, updatedAt: Date.now() };
  return { documents: [doc], activeId: doc.id };
};

/** Build a workspace from the pre-tabs draft + recents keys. */
const migrateLegacyWorkspace = (): WorkspaceState => {
  const draft = localStorage.getItem(DRAFT_KEY);
  const legacyRecents = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? "[]");
  const documents: WorkspaceDocument[] = Array.isArray(legacyRecents) ? legacyRecents : [];

  if (draft === null && documents.length === 0) return freshWorkspace();

  const content = draft ?? "";
  let active = documents.find((doc) => doc.content === content);
  if (!active) {
    const title = loadPreferences().exportTitle;
    active = {
      id: createId(),
      name: title && title !== defaultPreferences.exportTitle ? title : inferTitle(content),
      content,
      updatedAt: Date.now(),
    };
    documents.unshift(active);
  }
  return { documents, activeId: active.id };
};

export const loadWorkspace = (): WorkspaceState => {
  try {
    const raw = localStorage.getItem(WORKSPACE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as WorkspaceState;
      if (Array.isArray(parsed.documents) && parsed.documents.length > 0) {
        const activeId = parsed.documents.some((doc) => doc.id === parsed.activeId)
          ? parsed.activeId
          : parsed.documents[0].id;
        return { documents: parsed.documents, activeId };
      }
    }
    return migrateLegacyWorkspace();
  } catch {
    return freshWorkspace();
  }
};

export const saveWorkspace = (state: WorkspaceState) => {
  localStorage.setItem(WORKSPACE_KEY, JSON.stringify(state));
};

export const loadVaultRoot = (): string | null => {
  try {
    return localStorage.getItem(VAULT_KEY);
  } catch {
    return null;
  }
};

export const saveVaultRoot = (root: string | null) => {
  if (root) localStorage.setItem(VAULT_KEY, root);
  else localStorage.removeItem(VAULT_KEY);
};

/** Remove everything Markdown Studio stores in this browser. */
export const clearAllData = () => {
  for (const key of [WORKSPACE_KEY, PREFS_KEY, DRAFT_KEY, RECENTS_KEY, VAULT_KEY]) {
    localStorage.removeItem(key);
  }
};
